import type { PqrEditableMaterialRow } from './pqrPreviewDiff'

/**
 * Client-side mirror of `mapMaterials` in `server/src/pdf/pqrDataMapper.ts`.
 * Walks `sections.base.manufacturerLots[*].pipes[*]` and produces one editable
 * row per pipe with the first-of-array lot fields propagated onto the row.
 *
 * Index keys (`lotIndex`, `pipeIndex`) are preserved so the diff util can
 * build a sparse server patch that targets the exact JSON locations.
 *
 * When `weldId` is supplied (per-weld scoping introduced in round 2 of
 * `pqr-five-changes`), the function emits exactly TWO rows — Pipe 1 from
 * the weld's `pupOne` heat lookup, Pipe 2 from `pupTwo` — to match what
 * the canvas / PDF show. This keeps the editor and the preview in sync;
 * before this change, the canvas showed 2 scoped pipes but the modal
 * still listed every pipe across every lot, which the user called out.
 *
 * Fallback chain for scoped mode (per pup):
 *   1. exact heat match in `base.manufacturerLots[].pipes[].heatNumber`
 *   2. first lot / first pipe (best-effort fill so the editor still
 *      surfaces useful data for welds whose pup assignments haven't
 *      been captured yet — mirrors the server's `mapMaterials`)
 *   3. no lots at all -> empty row keyed at (0, 0).
 */
export interface MaterialPipeSetDims {
  nominalDiameter: string
  nominalThickness: string
}

function firstOrEmpty(value: unknown): string {
  if (Array.isArray(value)) {
    const v = value[0]
    return typeof v === 'string' ? v : v == null ? '' : String(v)
  }
  return typeof value === 'string' ? value : ''
}

interface LotPipeIdx {
  lotIndex: number
  pipeIndex: number
  materialSpec: string
  typeGrade: string
  pipeManufacturer: string
  heatNumber: string
}

/** Walk every lot/pipe pair once, with materialised lot-fields. */
function enumerateLotPipes(lots: unknown[]): LotPipeIdx[] {
  const out: LotPipeIdx[] = []
  lots.forEach((lotRaw, lotIndex) => {
    if (!lotRaw || typeof lotRaw !== 'object') return
    const lot = lotRaw as {
      materialSpec?: unknown
      typeGrade?: unknown
      pipeManufacturer?: unknown
      pipes?: unknown
    }
    const materialSpec = firstOrEmpty(lot.materialSpec)
    const typeGrade = firstOrEmpty(lot.typeGrade)
    const pipeManufacturer = firstOrEmpty(lot.pipeManufacturer)
    const pipes = Array.isArray(lot.pipes) ? lot.pipes : []
    pipes.forEach((pipeRaw, pipeIndex) => {
      const pipe =
        pipeRaw && typeof pipeRaw === 'object'
          ? (pipeRaw as { heatNumber?: unknown })
          : { heatNumber: '' }
      out.push({
        lotIndex,
        pipeIndex,
        materialSpec,
        typeGrade,
        pipeManufacturer,
        heatNumber: typeof pipe.heatNumber === 'string' ? pipe.heatNumber : ''
      })
    })
  })
  return out
}

/** Find weld entry by weldId in sections.floorsheet.welds. */
function findWeldByWeldId(
  sections: Record<string, unknown>,
  weldId: string
): { pupOne: string; pupTwo: string } | null {
  const target = weldId.trim()
  if (!target) return null
  const fs = (sections as { floorsheet?: unknown }).floorsheet
  if (!fs || typeof fs !== 'object') return null
  const welds = (fs as { welds?: unknown }).welds
  if (!Array.isArray(welds)) return null
  for (const raw of welds) {
    if (!raw || typeof raw !== 'object') continue
    const w = raw as { weldId?: unknown; pupOne?: unknown; pupTwo?: unknown }
    if (typeof w.weldId !== 'string' || w.weldId.trim() !== target) continue
    return {
      pupOne: typeof w.pupOne === 'string' ? w.pupOne.trim() : '',
      pupTwo: typeof w.pupTwo === 'string' ? w.pupTwo.trim() : ''
    }
  }
  return null
}

export function readMaterialsFromBaseSection(
  sections: Record<string, unknown> | undefined,
  weldId?: string | null
): PqrEditableMaterialRow[] {
  if (!sections || typeof sections !== 'object') return []
  const base = (sections as { base?: unknown }).base
  if (!base || typeof base !== 'object') return []
  const lots = (base as { manufacturerLots?: unknown }).manufacturerLots
  if (!Array.isArray(lots)) return []

  const allPipes = enumerateLotPipes(lots)

  // SCOPED: emit exactly two rows (Pipe 1, Pipe 2) sourced from the
  // weld's pupOne / pupTwo heat lookups so the modal matches the canvas.
  if (weldId && weldId.trim()) {
    // Defensive: if NO pipes have been captured project-wide there's
    // nothing meaningful to scope to. Returning [] mirrors the legacy
    // behaviour and lets the modal render its "no lots" empty state
    // (which also points the user to the WPS Base Material form).
    if (allPipes.length === 0) return []
    const weld = findWeldByWeldId(sections, weldId.trim())
    const pupOneHeat = weld?.pupOne ?? ''
    const pupTwoHeat = weld?.pupTwo ?? ''
    const fallback = allPipes[0]
    const resolvePup = (heat: string, label: string): PqrEditableMaterialRow => {
      const match = heat ? allPipes.find((p) => p.heatNumber.trim() === heat) : undefined
      const src = match ?? fallback
      return {
        lotIndex: src.lotIndex,
        pipeIndex: src.pipeIndex,
        materialSpec: src.materialSpec,
        typeGrade: src.typeGrade,
        pipeManufacturer: src.pipeManufacturer,
        // Heat resolution mirrors the server: prefer the weld's pup heat,
        // then the fallback pipe's heat, then empty so the editor still
        // shows something to start from.
        heatNumber: heat || src.heatNumber || '',
        label
      }
    }
    return [resolvePup(pupOneHeat, 'Pipe 1'), resolvePup(pupTwoHeat, 'Pipe 2')]
  }

  // LEGACY (no scope): one row per pipe across every lot, preserved so
  // existing aggregated PQRs render identically.
  return allPipes.map((p, idx) => ({
    lotIndex: p.lotIndex,
    pipeIndex: p.pipeIndex,
    materialSpec: p.materialSpec,
    typeGrade: p.typeGrade,
    pipeManufacturer: p.pipeManufacturer,
    heatNumber: p.heatNumber,
    label: `Pipe ${idx + 1}`
  }))
}
